/**
 * Protocol Registry — central mapping of all supported VendorContent protocols.
 *
 * Derives from the FlatBuffers VendorContent union in cdo.fbs:
 *   Mce, Modbus, Ln1, Diligent, Bacnet, Rvu, Opc, LobbyVision, Cga, Sequent,
 *   NumatoGpio, NumatoRelay, Stm32, Ln2
 *
 * Removed: BEAGLE (deprecated), CANELEVATOR (deprecated)
 */

export interface ProtocolInfo {
  /** Display name shown in UI dropdowns */
  label: string;
  /** Value stored in adapter.protocol_type */
  value: string;
  /** Whether this protocol uses leading zeroes config */
  hasLeadingZeroes: boolean;
  /** Whether this protocol requires a CDO editor (false = no config needed) */
  hasCdoEditor: boolean;
  /** Brief description for field engineers */
  description: string;
}

/**
 * All supported protocols, ordered for dropdown display.
 * MCE first (most common), then alphabetical.
 */
export const PROTOCOL_REGISTRY: ProtocolInfo[] = [
  {
    label: "MCE",
    value: "MCE",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Motion Control Engineering elevator controller",
  },
  {
    label: "BACnet",
    value: "BACNET",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Building Automation and Control networks",
  },
  {
    label: "CGA",
    value: "CGA",
    hasLeadingZeroes: true,
    hasCdoEditor: true,
    description: "CGA elevator controller (LN1-based)",
  },
  {
    label: "Diligent",
    value: "DILIGENT",
    hasLeadingZeroes: false,
    hasCdoEditor: false,
    description: "Diligent elevator controller (no config required)",
  },
  {
    label: "LN1",
    value: "LN1",
    hasLeadingZeroes: true,
    hasCdoEditor: true,
    description: "LiftNet 1 legacy protocol",
  },
  {
    label: "LN2",
    value: "LN2",
    hasLeadingZeroes: false,
    hasCdoEditor: false,
    description: "LiftNet 2 native protocol (no config required)",
  },
  {
    label: "LobbyVision",
    value: "LOBBYVISION",
    hasLeadingZeroes: true,
    hasCdoEditor: true,
    description: "LobbyVision elevator controller (LN1-based)",
  },
  {
    label: "LobbyVision 2",
    value: "LOBBYVISION2",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "LobbyVision 2 elevator controller (binary telegraph protocol)",
  },
  {
    label: "Modbus",
    value: "MODBUS",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Modbus industrial communication protocol",
  },
  {
    label: "Numato GPIO",
    value: "NUMATOGPIO",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Numato GPIO relay board",
  },
  {
    label: "Numato Relay",
    value: "NUMATORELAY",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Numato Relay board",
  },
  {
    label: "OPC",
    value: "OPC",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "OPC DA (Classic / COM)",
  },
  {
    label: "OPC UA",
    value: "OPCUA",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "OPC UA (opc.tcp secure channel, NodeId subscriptions)",
  },
  {
    label: "RVU",
    value: "RVU",
    hasLeadingZeroes: true,
    hasCdoEditor: true,
    description: "RVU elevator controller (LN1-based)",
  },
  {
    label: "Sequent",
    value: "SEQUENT",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "Sequent Microsystems HAT for Raspberry Pi",
  },
  {
    label: "STM32",
    value: "STM32",
    hasLeadingZeroes: false,
    hasCdoEditor: true,
    description: "STM32 microcontroller board",
  },
];

/** Lookup a protocol by its value string */
export const getProtocolInfo = (value: string): ProtocolInfo | undefined =>
  PROTOCOL_REGISTRY.find((p) => p.value === value);

/** Check if a protocol uses leading zeroes */
export const protocolHasLeadingZeroes = (value: string): boolean =>
  getProtocolInfo(value)?.hasLeadingZeroes ?? false;

/** Check if a protocol needs a CDO editor */
export const protocolHasCdoEditor = (value: string): boolean =>
  getProtocolInfo(value)?.hasCdoEditor ?? false;

/** Protocol values only, for use in selectors */
export const PROTOCOL_VALUES = PROTOCOL_REGISTRY.map((p) => p.value);
